/**
 * Shared topmost-modal stack.
 *
 * Both the shared <Dialog> component and the `useSubmitShortcut` hook (used by
 * modals that render their own `fixed inset-0` overlay) register here while they
 * are open. Keyboard shortcuts — ESC to close, Cmd/Ctrl+Enter to submit — then
 * act only on the frontmost modal.
 *
 * Without a shared stack, a self-overlay parent modal and its child (both using
 * window-level key listeners) would both fire on Cmd/Ctrl+Enter, because
 * `stopPropagation()` cannot stop sibling listeners on the same window node.
 * Routing every modal through one stack makes nesting — including mixed
 * Dialog + custom-overlay nesting — behave correctly.
 */

let stack: symbol[] = [];

/** Register a modal as opened. Call when the modal becomes visible. */
export function pushModal(id: symbol): void {
  stack.push(id);
}

/** Unregister a modal. Call when the modal closes/unmounts. */
export function popModal(id: symbol): void {
  stack = stack.filter((entry) => entry !== id);
}

/** True only when `id` is the frontmost open modal. */
export function isTopmostModal(id: symbol): boolean {
  return stack.length > 0 && stack[stack.length - 1] === id;
}
